/**
 * Socket.IO Context
 * Gerencia conexão WebSocket para push notifications em tempo real
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

// Deploy unificado: Socket.IO no mesmo servidor
const getSocketUrl = () => {
    const isDev = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.startsWith('192.168.') ||
        window.location.hostname.startsWith('10.') ||
        window.location.hostname.startsWith('172.');

    return isDev
        ? 'http://localhost:9191'
        : window.location.origin;
};

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket deve ser usado dentro de SocketProvider');
    }
    return context;
};

export const SocketProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const reconnectAttempts = useRef(0);
    const maxReconnectAttempts = 5;

    // Obter token diretamente do localStorage (onde é armazenado pelo AuthContext)
    const getToken = () => localStorage.getItem('token');

    // Listeners registrados por componentes
    const messageListeners = useRef(new Set());
    const notificationListeners = useRef(new Set());
    const contactUpdateListeners = useRef(new Set());

    // Conectar ao Socket.IO quando usuário estiver logado
    useEffect(() => {
        // Aguarda autenticação terminar antes de conectar
        if (authLoading) return;

        const token = getToken();

        if (!user || !token) {
            // Desconectar se não houver usuário (silenciosamente se nunca conectou)
            if (socket) {
                socket.disconnect();
                setSocket(null);
                setIsConnected(false);
            }
            return;
        }

        // Criar conexão Socket.IO
        const socketUrl = getSocketUrl();
        const newSocket = io(socketUrl, {
            auth: {
                token: token
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: maxReconnectAttempts,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000
        });

        // Event: Conectado
        newSocket.on('connect', () => {
            setIsConnected(true);
            setConnectionError(null);
            reconnectAttempts.current = 0;
        });

        // Event: Confirmação de conexão do servidor
        newSocket.on('connected', (_data) => {});

        // Event: Desconectado
        newSocket.on('disconnect', (_reason) => {
            setIsConnected(false);
        });

        // Event: Erro de conexão
        newSocket.on('connect_error', (error) => {
            console.error('❌ Socket.IO: Erro de conexão:', error.message);
            setConnectionError(error.message);
            reconnectAttempts.current++;

            if (reconnectAttempts.current >= maxReconnectAttempts) {
                console.error('Socket.IO: Max reconnection attempts reached');
            }
        });

        // Event: Nova mensagem
        newSocket.on('new_message', (payload) => {
            messageListeners.current.forEach(listener => {
                try {
                    listener(payload.data, payload.timestamp);
                } catch (err) {
                    console.error('Erro no listener de mensagem:', err);
                }
            });
        });

        // Event: Atualização de contato
        newSocket.on('contact_update', (payload) => {
            // Notificar todos os listeners de contato
            contactUpdateListeners.current.forEach(listener => {
                try {
                    listener(payload.data, payload.timestamp);
                } catch (err) {
                    console.error('Erro no listener de contato:', err);
                }
            });
        });

        // Event: Nova notificação de handoff
        newSocket.on('new_notification', (payload) => {
            // Notificar todos os listeners de notificação
            notificationListeners.current.forEach(listener => {
                try {
                    listener(payload.data, payload.timestamp);
                } catch (err) {
                    console.error('Erro no listener de notificação:', err);
                }
            });
        });

        // Event: Pong (keep-alive)
        newSocket.on('pong', (data) => {
            // Keep-alive silencioso
        });

        setSocket(newSocket);

        // Cleanup na desmontagem
        return () => {
            newSocket.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, authLoading]); // Only reconnect when user ID or auth loading changes

    // Registrar listener para novas mensagens
    const onNewMessage = useCallback((callback) => {
        messageListeners.current.add(callback);
        // Retornar função para remover listener
        return () => {
            messageListeners.current.delete(callback);
        };
    }, []);

    // Registrar listener para atualizações de contato
    const onContactUpdate = useCallback((callback) => {
        contactUpdateListeners.current.add(callback);
        return () => {
            contactUpdateListeners.current.delete(callback);
        };
    }, []);

    // Registrar listener para notificações de handoff
    const onNewNotification = useCallback((callback) => {
        notificationListeners.current.add(callback);
        return () => {
            notificationListeners.current.delete(callback);
        };
    }, []);

    // Enviar ping manual (para debug)
    const sendPing = useCallback(() => {
        if (socket && isConnected) {
            socket.emit('ping');
        }
    }, [socket, isConnected]);

    const value = {
        socket,
        isConnected,
        connectionError,
        onNewMessage,
        onContactUpdate,
        onNewNotification,
        sendPing
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketContext;
